/* eslint-disable react-hooks/exhaustive-deps */
/*
  AdminExport — Stage E acquisition-grade IP export.

  Three panes:
    1. Preview — show counts of what would be exported
    2. Download — kick off an encrypted ZIP download (3/hour rate-limited)
    3. API keys — issue, list, revoke scoped pull keys
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Download, Key, Eye, FileLock, AlertTriangle, Copy, X } from "lucide-react";

const SCOPES = ["archetypes", "ontology", "outcomes", "embeddings"];

function PreviewPane() {
  const [manifest, setManifest] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => {
    try { setManifest((await api.get("/admin/export/preview")).data); }
    catch (e) { setErr(e?.response?.data?.detail || "Preview failed"); }
  };
  useEffect(() => { load(); }, []);

  if (err) return <p className="text-rose text-sm">{err}</p>;
  if (!manifest) return <p className="text-cocoa/50 text-sm">Loading manifest…</p>;
  const c = manifest.counts || {};

  return (
    <section data-testid="export-preview" className="ss-card p-6">
      <div className="flex items-center gap-2">
        <Eye size={14} className="text-mauve" />
        <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Export preview</p>
      </div>
      <h2 className="font-heading text-2xl text-cocoa mt-1">What's in the package.</h2>
      <p className="text-cocoa/65 text-sm mt-1">
        Anonymisation pipeline: <code>{manifest.anonymisation_pipeline}</code> · Schema v{manifest.schema_version}
      </p>
      <div className="mt-5 grid sm:grid-cols-3 gap-4">
        {[
          ["Archetypes (versioned)", c.archetypes_versioned],
          ["Ontology snapshots",     c.ontology_snapshots],
          ["Anonymised embeddings",  c.embeddings],
          ["Active prompts",         c.prompt_active],
          ["Prompt runs (30d)",      c.prompt_runs_aggregates],
          ["Outcomes by archetype",  c.outcomes_aggregates],
        ].map(([label, val]) => (
          <div key={label} className="ss-card p-4 bg-blush/10">
            <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/55">{label}</p>
            <p className="font-heading text-3xl text-cocoa mt-1">{val ?? 0}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function DownloadPane() {
  const [includeEmb, setIncludeEmb] = useState(true);
  const [limit, setLimit] = useState(5000);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try { setHistory((await api.get("/admin/export/recent")).data?.items || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const download = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/admin/export/download",
        { include_embeddings: includeEmb, embeddings_limit: limit, label: label || undefined },
        { responseType: "blob" });
      // Trigger browser save
      const cd = r.headers["content-disposition"] || "";
      const filename = /filename="(.+?)"/.exec(cd)?.[1] || "sugarsense-export.zip";
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setMsg(`Downloaded ${filename}.`);
      await loadHistory();
    } catch (e) {
      setMsg(e?.response?.status === 429 ? "Rate-limited — try again later (3/hour)." : "Download failed.");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="export-download" className="ss-card p-6 mt-6">
      <div className="flex items-center gap-2">
        <Download size={14} className="text-mauve" />
        <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Encrypted ZIP download</p>
      </div>
      <h2 className="font-heading text-2xl text-cocoa mt-1">Snapshot the IP package.</h2>
      <p className="text-cocoa/65 text-sm mt-1">
        Generates an in-process ZIP of the entire export-eligible dataset. If
        <code> EXPORT_PASSPHRASE</code> is set in the environment, the ZIP is wrapped in AES-256-GCM (scrypt key, authenticated)
        (extension <code>.aes</code>). Otherwise the ZIP downloads raw. Rate-limited to 3/hour per admin.
      </p>
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="export-include-embeddings" type="checkbox" checked={includeEmb} onChange={(e) => setIncludeEmb(e.target.checked)} />
          Include embeddings
        </label>
        <label className="text-xs text-cocoa/55">
          Embeddings limit
          <input data-testid="export-limit" type="number" value={limit} min="100" max="20000" step="100"
            onChange={(e) => setLimit(parseInt(e.target.value || "0", 10))}
            className="mt-1 w-full bg-ivory border border-champagne rounded-md h-9 px-2 text-sm" />
        </label>
        <label className="text-xs text-cocoa/55">
          Optional label
          <input data-testid="export-label" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. 'Q1-2026 due-diligence'"
            className="mt-1 w-full bg-ivory border border-champagne rounded-md h-9 px-2 text-sm" />
        </label>
      </div>
      <button data-testid="export-download-btn" disabled={busy} onClick={download}
        className="mt-4 ss-pill ss-pill-primary h-10 px-5 disabled:opacity-50">
        <Download size={13} /> {busy ? "Packaging…" : "Generate & download ZIP"}
      </button>
      <button data-testid="export-binder-btn" disabled={busy} onClick={async () => {
        setBusy(true); setMsg("");
        try {
          const r = await api.post("/admin/export/binder", {}, { responseType: "blob" });
          const cd = r.headers["content-disposition"] || "";
          const filename = /filename="(.+?)"/.exec(cd)?.[1] || "sugarsense-binder.pdf";
          const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
          const a = document.createElement("a");
          a.href = url; a.download = filename; a.click();
          URL.revokeObjectURL(url);
          setMsg(`Generated binder PDF (${filename}).`);
          await loadHistory();
        } catch (e) {
          setMsg(e?.response?.status === 429 ? "Rate-limited — 6 binders/hour." : "Binder generation failed.");
        } finally { setBusy(false); }
      }} className="mt-2 ml-2 ss-pill ss-pill-ghost h-10 px-5 disabled:opacity-50">
        <FileLock size={13} /> {busy ? "Generating…" : "Due-diligence binder (PDF)"}
      </button>
      {msg && <p className="mt-2 text-cocoa/70 text-sm">{msg}</p>}

      {history.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/55">Recent exports</p>
          <div className="mt-2 space-y-2 text-xs text-cocoa/70">
            {history.slice(0, 5).map((h) => (
              <div key={h.id} data-testid={`export-history-${h.id}`} className="flex justify-between gap-3 ss-card p-3">
                <span>{h.label || h.id.slice(0, 8)} <span className="text-cocoa/40">{h.created_at}</span></span>
                <span>{(h.size_bytes / 1024).toFixed(0)} KB{h.encrypted ? " · AES" : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function KeysPane() {
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState(["archetypes", "ontology", "outcomes"]);
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => { setKeys((await api.get("/admin/export/keys")).data?.items || []); };
  useEffect(() => { load(); }, []);

  const issue = async () => {
    if (!label || label.length < 2) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/export/keys", { label, scopes });
      setRevealed({ secret: r.data.secret, id: r.data.id, label: r.data.label });
      setLabel("");
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to issue key"); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Any consumer using it will lose access immediately.")) return;
    try { await api.post(`/admin/export/keys/${id}/revoke`); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Revoke failed"); }
  };

  const copy = (t) => navigator.clipboard.writeText(t);

  const toggleScope = (s) => setScopes((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  return (
    <section data-testid="export-keys" className="ss-card p-6 mt-6">
      <div className="flex items-center gap-2">
        <Key size={14} className="text-mauve" />
        <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Scoped pull API keys</p>
      </div>
      <h2 className="font-heading text-2xl text-cocoa mt-1">Read-only access for due-diligence partners.</h2>
      <p className="text-cocoa/65 text-sm mt-1">
        Each key is hashed at rest (SHA-256). Consumers authenticate against <code>GET /api/export/v1/{"{scope}"}</code> with
        <code> Authorization: Bearer ssk_xxx</code>. Scopes restrict which dataset slices the key can read.
      </p>

      <div className="mt-4 ss-card p-4 bg-blush/10">
        <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">Issue a new key</p>
        <input data-testid="export-key-label" value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="Key label (e.g. 'Acquirer-A due-diligence')"
          className="mt-2 w-full bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
        <div className="mt-2 flex gap-2 flex-wrap">
          {SCOPES.map((s) => (
            <button key={s} data-testid={`export-scope-${s}`} onClick={() => toggleScope(s)}
              className={`px-3 h-8 text-xs rounded-full ${scopes.includes(s) ? "bg-cocoa text-ivory" : "bg-champagne/40 text-cocoa/70"}`}>
              {s}
            </button>
          ))}
        </div>
        <button data-testid="export-key-issue" disabled={busy || label.length < 2 || scopes.length === 0}
          onClick={issue} className="mt-3 ss-pill ss-pill-primary h-10 px-5 disabled:opacity-50">
          <Key size={13} /> Issue key
        </button>
        {err && <p className="text-rose text-xs mt-2">{err}</p>}
      </div>

      {revealed && (
        <div data-testid="export-key-revealed" className="mt-4 ss-card-premium p-5 bg-sage/15 relative">
          <button onClick={() => setRevealed(null)} className="absolute top-3 right-3 text-cocoa/50"><X size={14} /></button>
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-mauve" />
            <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Save now — secret is shown once</p>
          </div>
          <p className="mt-2 text-cocoa text-sm">{revealed.label}</p>
          <div className="mt-3 flex items-center gap-2">
            <code className="ss-card px-3 py-2 text-xs flex-1 break-all">{revealed.secret}</code>
            <button data-testid="export-key-copy" onClick={() => copy(revealed.secret)}
              className="ss-pill ss-pill-ghost h-9 text-xs"><Copy size={12} /> Copy</button>
          </div>
          <p className="mt-2 text-[10px] text-cocoa/55">After closing this card the secret cannot be recovered — only its hash is stored.</p>
        </div>
      )}

      <div className="mt-5 space-y-2">
        {keys.map((k) => (
          <div key={k.id} data-testid={`export-key-${k.id}`} className={`ss-card p-4 flex items-center justify-between gap-3 ${k.revoked ? "opacity-50" : ""}`}>
            <div>
              <p className="text-cocoa">{k.label} {k.revoked && <span className="text-rose text-[10px] uppercase tracking-widest">revoked</span>}</p>
              <p className="text-[10px] tracking-widest uppercase text-cocoa/55">
                {(k.scopes || []).join(" · ")} · Last used: {k.last_used_at || "never"}
              </p>
            </div>
            {!k.revoked && (
              <button data-testid={`export-key-revoke-${k.id}`} onClick={() => revoke(k.id)}
                className="ss-pill ss-pill-ghost h-8 text-[11px]"><FileLock size={11} /> Revoke</button>
            )}
          </div>
        ))}
        {keys.length === 0 && <p className="text-cocoa/50 text-sm">No keys issued yet.</p>}
      </div>
    </section>
  );
}

export function AdminExport() {
  return (
    <div data-testid="admin-export">
      <PreviewPane />
      <DownloadPane />
      <KeysPane />
    </div>
  );
}
